import { localStorageProvider } from "../../../../AppController";

export const CircadiaEngineProviderFactory: ICircadiaEngineProviderFactory = ({
  circadia,
}) => {
  let gColor1 = "";
  let gColor2 = "";

  const windowCircadiaStatus = !!circadia;
  const isWindowCircadia = () => {
    return windowCircadiaStatus;
  };
  const isIOS = !!circadia && circadia.plat === "ios";

  // VoIP capability detection. Because the interface version (intfv) was kept
  // at 7, it can't distinguish a build that predates the loudness/route
  // functions from one that includes them — an older binary still reports
  // intfv === 7 but simply won't have these properties, and calling a missing
  // one throws synchronously. So we feature-detect against the RAW native
  // object here, before the web stub fallback below would mask it.
  const voipCanSetRxGain =
    !!circadia && typeof circadia.voipSetRxGain === "function";
  const voipCanSetAudioRoute =
    !!circadia && typeof circadia.voipSetAudioRoute === "function";

  const isAndroid = !!circadia && circadia.plat === "android";

  // VoIP works on any native shell (iOS or Android) that actually registered
  // the calling bridge — the native side compiles voipStartCall in for both
  // platforms. Feature-detect against the RAW native object: the web fallback
  // stub below also defines voipStartCall, so detecting after the fallback
  // would wrongly report support on plain web.
  const voipSupported =
    !!circadia &&
    (circadia.plat === "ios" || circadia.plat === "android") &&
    typeof circadia.voipStartCall === "function";

  if (!circadia) {
    circadia = {
      saveConfig: async () => {
        return true;
      },
      createSSPin: async () => {
        return true;
      },
      changeSSPin: async () => {
        return true;
      },
      saveSSValue: async (email: string, key: string, value: string) => {
        const storeValue = {
          email,
          key,
          value,
        };
        try {
          localStorageProvider.set("saveSSvalue", JSON.stringify(storeValue));
          return true;
        } catch {
          return Promise.reject();
        }
      },
      loadSSValue: async (email: string, key: string, value: string) => {
        const storeValue = {
          email,
          key,
          value,
        };
        const getSSvalue = JSON.parse(
          localStorageProvider.get("saveSSvalue") || ""
        );
        return storeValue.value === getSSvalue.value ? true : Promise.reject();
      },
      cognitoProvidePin: async () => {
        return true;
      },
      setStyleSheet: async () => {
        return true;
      },
      setNotifTokenCb: (cb: (token: string) => void) => {
        const token = "dummy-token";
        cb(token);
        return undefined;
      },
      loadSSValueBio: async () => {
        return false;
      },
      loginIntercomUser: async (
        token: string,
        hmac: string,
        userInfo: {
          name: string;
          facility: string;
          user_id?: string;
          email?: string;
        }
      ) => {
        let response = null;
        if (isWindowCircadia()) {
          // TODO: remove then or await
          await circadia
            .loginIntercomUser(token, hmac, userInfo)
            .then((res: string) => {
              response = { code: "success", message: res };
            })
            .catch((err: { message: string }) => {
              response = { code: "error", message: err.message };
            });
        }
        return response;
      },
      logoutIntercomUser: async () => {
        let response = null;
        if (isWindowCircadia()) {
          // TODO: remove then or await
          await circadia
            .logoutIntercomUser()
            .then((res: string) => {
              response = { code: "success", message: res };
            })
            .catch((err: { message: string }) => {
              response = { code: "error", message: err.message };
            });
        }
        return response;
      },
      showIntercom: async () => {
        let response = null;
        if (circadia) {
          await circadia
            .showIntercom()
            .then((res: boolean) => {
              response = { code: "success", message: res };
            })
            .catch((err: { message: string }) => {
              response = { code: "error", message: err.message };
            });
        }
        return response;
      },
      hideIntercom: async () => {
        let response = null;
        if (isWindowCircadia()) {
          await circadia
            .hideIntercom()
            .then((res: boolean) => {
              response = { code: "success", message: res };
            })
            .catch((err: { message: string }) => {
              response = { code: "error", message: err.message };
            });
        }
        return response;
      },
      // VoIP is iOS-only (intfv >= 7). On web / unsupported builds these are
      // safe no-ops; callers must feature-detect via `plat`/`intfv` first.
      voipStartCall: async () => {
        return "true";
      },
      voipStartTx: async () => {
        return "true";
      },
      voipStopTx: async () => {
        return "true";
      },
      voipSetVolume: async () => {
        return "true";
      },
      voipSetRxGain: async () => {
        return "true";
      },
      voipSetAudioRoute: async () => {
        return "true";
      },
      voipHangup: async () => {
        return "true";
      },
      setVoipStateCb: () => {},
      setVoipStatusCb: () => {},
      cognitoProvideMfa: () => {},
      cognitoProvideEmailOTP: () => {},
      cognitoUserLogout: () => {},
      setCognitoLoginFailedCb: () => {},
      setCognitoLoginDoneCb: () => {},
      setCognitoMfaRequiredCb: () => {},
      setCognitoPinRequiredCb: () => {},
      setCognitoEmailOTPRequiredCb: () => {},
      cognitoTokens: async () => {
        return { AccessToken: "", IdToken: "" };
      },
      playSound: () => {},
      cognitoUserLogin: () => {},
      cognitoUserEmailOTPLogin: () => {},
      cognitoProvideNewPassword: () => {},
      setCognitoNewPasswordRequiredCb: () => {},
      plat: "web",
      cognitoChangePassword: () => {
        return { mdg: "", code: 0 };
      },
      intfv: -1,
    };
  }

  const playSound = async (sound: string) => {
    if (isWindowCircadia()) {
      return circadia.playSound(sound);
    }
    return true;
  };

  const getDeviceToken = async () =>
    new Promise<string>((resolve) => {
      if (!circadia) {
        return resolve("");
      }

      circadia.setNotifTokenCb((token) => resolve(token));
      return "";
    });

  const changeAppTopAndBottom = (color1: string, color2: string) => {
    if (color1 === gColor1 && color2 === gColor2) return;
    gColor1 = color1;
    gColor2 = color2;

    if (!circadia) {
      return;
    }
    circadia.setStyleSheet(
      `background-color:qlineargradient(spread:pad, x1:0, y1:0, x2:0, y2:1, stop:0.000 ${color1},stop:0.250 ${color1} ,stop:0.750 ${color2}, stop:1.000 ${color2});`
    );
  };

  const loginIntercomUser = async (
    token: string,
    hmac: string,
    userInfo: {
      name: string;
      facility: string;
      user_id?: string;
      email?: string;
    }
  ) => {
    let response = null;
    if (isWindowCircadia()) {
      await circadia
        .loginIntercomUser(token, hmac, userInfo)
        .then((res: string) => {
          response = { code: "success", message: res };
        })
        .catch((err: { message: string }) => {
          response = { code: "error", message: err.message };
        });
    }
    return response;
  };

  const logoutIntercomUser = async () => {
    let response = null;
    if (isWindowCircadia()) {
      await circadia
        .logoutIntercomUser()
        .then((res: string) => {
          response = { code: "success", message: res };
        })
        .catch((err: { message: string }) => {
          response = { code: "error", message: err.message };
        });
    }
    return response;
  };
  const showIntercom = async () => {
    let response = null;
    if (isWindowCircadia()) {
      await circadia
        .showIntercom()
        .then((res: boolean) => {
          response = { code: "success", message: res };
        })
        .catch((err: { message: string }) => {
          response = { code: "error", message: err.message };
        });
    }
    return response;
  };
  const hideIntercom = async () => {
    let response = null;
    if (isWindowCircadia()) {
      await circadia
        .hideIntercom()
        .then((res: boolean) => {
          response = { code: "success", message: res };
        })
        .catch((err: { message: string }) => {
          response = { code: "error", message: err.message };
        });
    }
    return response;
  };

  return {
    ...circadia,
    changeAppTopAndBottom,
    getDeviceToken,
    hideIntercom,
    showIntercom,
    logoutIntercomUser,
    loginIntercomUser,
    isWindowCircadia,
    playSound,
    isIOS,
    isAndroid,
    voipSupported,
    voipCanSetRxGain,
    voipCanSetAudioRoute,
  };
};
